<template>
  <v-container>
    <v-row justify="center">
      <!--<editor-fold desc="Table">-->
      <v-col cols="8">
        <div class="title text-uppercase font-weight-medium">
          Attributes Management
        </div>

        <div
          class="mt-5 grey-bg"
          style="position: sticky; top: 68px; z-index: 2"
        >
          <v-row
            no-gutters
            align="center"
          >
            <v-col cols="auto">
              <v-tabs
                v-model="currentTab"
                background-color="grey-bg"
              >
                <v-tab
                  v-for="attribute in attributes"
                  :key="attribute.id"
                  class="text-none"
                  :href="'#'+attribute.id"
                >
                  {{ attribute.title }}
                </v-tab>
              </v-tabs>
            </v-col>
            <v-spacer />
            <v-col
              cols="auto"
              class="px-3"
            >
              <div v-if="saving">
                <v-progress-circular
                  :width="1"
                  indeterminate
                  :size="16"
                  color="primary"
                />
              </div>
              <div
                v-else-if="saved"
                class="success--text caption"
              >
                New order saved
                <v-icon
                  right
                  x-small
                  color="success"
                >
                  fa fa-check
                </v-icon>
              </div>
            </v-col>
          </v-row>
          <v-divider class="mb-2" />
        </div>

        <v-row no-gutters>
          <v-col cols="8">
            <draggable
              v-model="attributeValues"
              :force-fallback="true"
              handle=".draghandle"
              @change="onChanged"
            >
              <transition-group
                type="transition"
                name="picker"
              >
                <v-card
                  v-for="av in attributeValues"
                  :key="av.order_position"
                  class="mb-1"
                >
                  <v-card-text class="pa-3">
                    <v-row
                      no-gutters
                      align="center"
                    >
                      <v-col
                        cols="auto"
                        class="pl-2 pr-6 draghandle"
                      >
                        <v-icon color="grey">
                          fa fa-bars
                        </v-icon>
                      </v-col>
                      <v-col v-if="!av.editing">
                        {{ av.value }}
                        <v-btn
                          small
                          icon
                          @click="av.editing = true"
                        >
                          <v-icon x-small>
                            fa fa-pen
                          </v-icon>
                        </v-btn>
                      </v-col>
                      <v-col
                        v-else
                        class="text-no-wrap"
                      >
                        <v-text-field
                          v-model="av.value"
                          dense
                          outlined
                          style="width: 200px"
                          hide-details
                          autofocus
                          class="d-inline-block"
                          @keyup.enter="saveValue(av)"
                        />
                        <v-btn
                          text
                          color="primary"
                          @click.prevent.stop="saveValue(av)"
                        >
                          <v-icon
                            left
                            small
                          >
                            fa fa-check
                          </v-icon>
                          Save
                        </v-btn>
                        <v-btn
                          text
                          color="grey"
                          icon
                          @click.prevent.stop="av.value = av.original_value; av.editing = false"
                        >
                          <v-icon
                            small
                          >
                            fa fa-times
                          </v-icon>
                        </v-btn>
                      </v-col>
                      <v-col
                        cols="auto"
                        class="px-3 text-no-wrap"
                      >
                        <v-chip
                          outlined
                          small
                          :color="av.playlists_count ? 'primary' : 'grey lighten-3'"
                          class="mr-2"
                          :to="{ name: 'playlists', query: getLinkQuery(av.id, currentAttributeId)}"
                        >
                          {{ av.playlists_count }} playlists
                        </v-chip>
                        <v-chip
                          outlined
                          small
                          :color="av.library_files_count ? 'primary' : 'grey lighten-3'"
                          :to="{ name: 'musicLibrary', query: getLinkQuery(av.id, currentAttributeId) }"
                        >
                          {{ av.library_files_count }} songs
                        </v-chip>
                      </v-col>
                      <v-col cols="auto">
                        <v-tooltip
                          top
                          transition="fade-transition"
                          max-width="280px"
                        >
                          <template v-slot:activator="{ on }">
                            <div v-on="on">
                              <v-btn
                                color="error"
                                small
                                icon
                                :disabled="av.playlists_count > 0 || av.library_files_count > 0"
                                @click="deleteId = av.id; deleteConfirmationDialogVisible = true"
                              >
                                <v-icon
                                  x-small
                                >
                                  fa fa-trash
                                </v-icon>
                              </v-btn>
                            </div>
                          </template>
                          <span v-if="av.playlists_count > 0 || av.library_files_count > 0">
                            Not allowed - attribute is associated with songs or playlists.
                          </span>
                          <span v-else>
                            Delete attribute
                          </span>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </transition-group>
            </draggable>
            <v-card>
              <v-card-text class="pa-3">
                <v-text-field
                  v-model="newValue"
                  outlined
                  dense
                  style="width: 250px"
                  class="ml-10 d-inline-block"
                  placeholder="Add new attribute"
                  hide-details
                  @keyup.enter="saveNew()"
                />
                <v-btn
                  text
                  color="primary"
                  @click="saveNew()"
                >
                  <v-icon
                    small
                    left
                  >
                    fa fa-check
                  </v-icon>
                  Save
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete the attribute?"
      @confirm="deleteValue()"
    />

    <v-footer
      v-if="changed"
      fixed
      color="primary"
      padless
      elevation="10"
      class="no-gutters"
    >
      <v-col>
        <div class="white--text pa-3 body-2">
          <v-row
            no-gutters
            align="center"
          >
            <v-col />
          </v-row>
        </div>
      </v-col>
    </v-footer>
  </v-container>
</template>

<script>
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import Attributes from '@/services/Attributes'
import Draggable from 'vuedraggable'

export default {
  name: 'Attributes',
  components: {
    ConfirmDeleteDialog,
    Draggable
  },
  mixins: [deleteConfirmationMixin],
  data() {
    return {
      attributes: [],
      currentAttributeId: undefined,
      attributeValues: [],
      changed: false,
      saving: false,
      saved: false,
      newValue: '',
      deleteId: undefined,
      deleting: false
    }
  },
  computed: {
    currentTab: {
      set(value) {
        this.currentAttributeId = parseInt(value)
      },
      get() {
        if (!this.currentAttributeId) return undefined

        return this.currentAttributeId.toString()
      }
    }
  },
  watch: {
    currentAttributeId(newId) {
      const attributes = this.attributes.find(a => a.id === newId)

      if (attributes) {
        this.attributeValues = attributes.values.map((v, index) => {
          return {
            ...v,
            original_value: v.value,
            order_position: index + 1,
            editing: false
          }
        })

        return
      }

      this.attributeValues = []
    }
  },
  created() {
    this.fetchAttributes()
  },
  methods: {
    async fetchAttributes() {
      try {
        this.attributes = await Attributes.fetch()

        this.currentAttributeId = this.attributes[0].id
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There was an error getting attributes',
          color: 'error'
        })

        throw e
      }
    },
    onChanged() {
      this.save()
    },
    async save() {
      this.saving = true

      try {
        let position = 0

        const values = this.attributeValues.map(v => {
          position++

          return {
            ...v,
            order_position: position
          }
        })

        await Attributes.reorder(this.currentAttributeId, values)

        this.saved = true

        setTimeout(() => {
          this.saved = false
        }, 2000)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong saving order',
          color: 'error'
        })

        throw e
      } finally {
        this.saving = false
      }
    },
    getLinkQuery(attributeValueId, attributeId) {
      const attributes = {}

      attributes[attributeId] = [attributeValueId]

      return {
        attributes
      }
    },
    async saveValue(valueObject) {
      try {
        await Attributes.updateAttributeValue(valueObject.id, valueObject.value)

        valueObject.editing = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong updating',
          color: 'error'
        })

        throw e
      }
    },
    async saveNew() {
      try {
        if (!this.newValue) return

        const av = await Attributes.createAttributeValue(this.currentAttributeId, this.newValue)

        this.attributeValues.push({
          id: av.id,
          value: av.value,
          original_value: av.value,
          editing: false,
          library_files_count: 0,
          order_position: av.order_position,
          playlists_count: 0
        })

        this.attributes = await Attributes.fetch()

        this.newValue = ''
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong updating',
          color: 'error'
        })

        throw e
      }
    },
    async deleteValue() {
      this.deleting = true

      try {
        await Attributes.deleteValue(this.deleteId)

        const index = this.attributeValues.findIndex(av => av.id === this.deleteId)

        if (index > -1) {
          this.attributeValues.splice(index, 1)
        }

        this.$store.dispatch('alert', {
          text: 'Attribute deleted',
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong updating',
          color: 'error'
        })

        throw e
      } finally {
        this.deleting = false
        this.deleteConfirmationDialogVisible = false
      }
    }

  }
}
</script>

<style type="text/css">
.draghandle {
  cursor: grab;
}
</style>
